import { api, apiForm, fetchBlob } from './client'

/**
 * 교육 증빙 — 기획 = docs/2026-09-07_교육증빙-기획.md
 *
 * 급여명세서를 본으로 삼되 **없는 것 둘**이 있다. 화면에서도 그대로다.
 *   status / 회수    상태가 하나뿐이라 안 둔다. 잘못 올린 건 **지운다**
 *   viewedAt         교부 의무가 없어 열람을 증빙으로 안 쓴다 → "확인함" 배지가 없다
 *
 * 우리가 안 하는 것 — 교육 내용을 만들지 않고, 이수 여부를 판정하지 않고,
 * 법정 의무교육 목록을 갖지 않는다. **회사가 정하고 우리는 나른다.**
 */

export interface MyTraining {
  id: string
  title: string
  /** 회사가 고른 이수일 (YYYY-MM-DD) */
  completedOn: string
  fileName: string
  fileSize: number
  createdAt: string
}

export interface AdminTraining {
  id: string
  title: string
  completedOn: string
  fileName: string
  fileSize: number
  memo: string | null
  createdAt: string
  employee: {
    id: string
    name: string
    employeeNumber: string | null
  }
}

/** 파일명이 누구에게 갈지 — 올리기 전 확인용 */
export interface TrainingMatch {
  fileName: string
  employeeId: string | null
  employeeName: string | null
  employeeNumber: string | null
  /** 매칭 실패 이유. 성공이면 null */
  reason: string | null
}

/** 이 교육을 안 받은 사람. **사실 조회지 판정이 아니다** — 기한을 안 본다 */
export interface MissingEmployee {
  id: string
  name: string
  employeeNumber: string | null
  department: { name: string } | null
}

// ── 직원 ─────────────────────────────────────────────────────

export function getMyTrainings() {
  return api<MyTraining[]>('/api/trainings/my')
}

// ── 관리자·부서장 ────────────────────────────────────────────
//
// ⚠ 부서장은 **담당 부서만** 본다. 서버가 getManagedDepartmentIds 로 거른다.
//   화면에서 조직 전체를 그리는 것처럼 짜면 안 된다.

export function getTrainings(
  params: { title?: string; employeeId?: string; take?: number; skip?: number } = {},
) {
  const q = new URLSearchParams()
  if (params.title) q.set('title', params.title)
  if (params.employeeId) q.set('employeeId', params.employeeId)
  if (params.take) q.set('take', String(params.take))
  if (params.skip) q.set('skip', String(params.skip))
  return api<{ records: AdminTraining[]; total: number }>(`/api/trainings?${q.toString()}`)
}

/**
 * 이미 올라온 제목들.
 *
 * 🔴 이게 없으면 "안 받은 사람" 조회가 성립하지 않는다. "2026년 성희롱 예방교육" 과
 *   "성희롱예방교육(2026)" 이 다른 그룹이 되어 전원이 미이수로 나온다.
 *   우리가 정한 목록이 아니라 **그 회사가 쓴 제목**이라 원칙을 안 깬다.
 */
export function getTrainingTitles() {
  return api<string[]>('/api/trainings/titles')
}

export function getMissingEmployees(title: string) {
  return api<MissingEmployee[]>(`/api/trainings/missing?title=${encodeURIComponent(title)}`)
}

/**
 * 올리기 전 미리보기 — **파일은 아직 안 보낸다.** 이름만 보내서 누구에게 갈지 확인한다.
 * 급여명세서와 같은 2단계다. 잘못 붙이면 남의 이수증이 남에게 간다.
 */
export function previewTrainings(fileNames: string[]) {
  return api<TrainingMatch[]>('/api/trainings/preview', {
    method: 'POST',
    body: JSON.stringify({ fileNames }),
  })
}

export interface TrainingUploadResult {
  created: { fileName: string; employeeId: string; trainingId: string }[]
  failed: { fileName: string; reason: string }[]
}

export function uploadTrainings(input: {
  title: string
  completedOn: string
  memo?: string
  files: File[]
}) {
  const form = new FormData()
  form.append('title', input.title)
  form.append('completedOn', input.completedOn)
  if (input.memo) form.append('memo', input.memo)
  for (const f of input.files) form.append('files', f)
  return apiForm<TrainingUploadResult>('/api/trainings', form)
}

/** 되돌리는 유일한 길이다 (회수·취소가 없다). 파일까지 지운다 */
export function deleteTraining(id: string) {
  return api<{ id: string }>(`/api/trainings/${id}`, { method: 'DELETE' })
}

// ── 파일 열람 ────────────────────────────────────────────────

export async function openTrainingFile(id: string): Promise<{ url: string } | { error: string }> {
  const res = await fetchBlob(`/api/trainings/${id}/file`)
  if ('error' in res) return res
  return { url: URL.createObjectURL(res.blob) }
}

// ── 유틸 ─────────────────────────────────────────────────────

export function fileSizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`
}
